import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Alert, Stack,
  Avatar, Tooltip,
} from '@mui/material';
import { Add, Delete, Person, AdminPanelSettings, FitnessCenter } from '@mui/icons-material';
import { authAPI, gymsAPI } from '../services/api';
import PageHeader from '../components/PageHeader';

const ROLE_CONFIG = {
  admin:      { label: 'Admin',       color: 'error',   icon: <AdminPanelSettings fontSize="small" /> },
  trainer:    { label: 'Trainer',     color: 'primary', icon: <FitnessCenter fontSize="small" /> },
  gym_admin:  { label: 'Gym Admin',   color: 'warning', icon: <Person fontSize="small" /> },
  manager:    { label: 'Manager',      color: 'success', icon: <AdminPanelSettings fontSize="small" /> },
  coach:      { label: 'Coach',        color: 'info',    icon: <FitnessCenter fontSize="small" /> },
  sales_staff:{ label: 'Sales Staff',  color: 'secondary', icon: <Person fontSize="small" /> },
  front_desk: { label: 'Front Desk',   color: 'default', icon: <Person fontSize="small" /> },
};

export default function StaffManagement() {
  const [staff, setStaff] = useState([]);
  const [gyms, setGyms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'trainer', gymId: '' });
  const [saving, setSaving] = useState(false);

  const fetchStaff = async () => {
    try {
      const res = await authAPI.getStaff();
      setStaff(res.data.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load staff');
    }
  };

  const fetchGyms = async () => {
    try {
      const res = await gymsAPI.getAll();
      const list = res.data.data || [];
      setGyms(list);
      if (list.length > 0 && !form.gymId) {
        setForm(f => ({ ...f, gymId: list[0]._id }));
      }
    } catch (e) {
      console.error('Failed to load gyms:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
    fetchGyms();
  }, []);

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) {
      setError('All fields are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await authAPI.createStaff(form);
      setSuccess(`${ROLE_CONFIG[form.role].label} account created for ${form.email}`);
      setDialogOpen(false);
      setForm({ name: '', email: '', password: '', role: 'trainer', gymId: '' });
      fetchStaff();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to create account');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await authAPI.deleteStaff(deleteId);
      setSuccess('Staff account deleted');
      setDeleteId(null);
      fetchStaff();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to delete');
    }
  };

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title="Staff Management"
        subtitle="Create and manage admin and trainer accounts"
        gradient="linear-gradient(90deg, #0891B2, #22D3EE)"
        actions={
          <Button variant="contained" size="small" startIcon={<Add />} onClick={() => setDialogOpen(true)}>Add Staff</Button>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
        <Table sx={{ minWidth: 700 }}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right" sx={{ pr: 2.5, minWidth: 120 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} align="center">Loading...</TableCell></TableRow>
            ) : staff.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>
                No staff accounts yet. Create the first one.
              </TableCell></TableRow>
            ) : staff.map((user) => {
              const cfg = ROLE_CONFIG[user.role] || { label: user.role, color: 'default' };
              return (
                <TableRow key={user._id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
                        {user.name[0].toUpperCase()}
                      </Avatar>
                      <Typography variant="body2" fontWeight={600}>{user.name}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell><Typography variant="body2">{user.email}</Typography></TableCell>
                  <TableCell>
                    <Chip label={cfg.label} color={cfg.color} size="small" icon={cfg.icon} variant="outlined" sx={{ borderColor: `${cfg.color}.main` }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Delete account">
                      <IconButton size="small" color="error" onClick={() => setDeleteId(user._id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Staff Account</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Full Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} fullWidth required />
            <TextField label="Email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} fullWidth required />
            <TextField label="Password" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} fullWidth required helperText="Minimum 6 characters" />
            <TextField select label="Role" value={form.role} onChange={e => setForm({...form, role: e.target.value})} fullWidth>
              <MenuItem value="trainer">Trainer — can create workout & meal plans</MenuItem>
              <MenuItem value="coach">Coach — supports member training and content</MenuItem>
              <MenuItem value="sales_staff">Sales Staff — manages leads, retention, and invoices</MenuItem>
              <MenuItem value="front_desk">Front Desk — manages attendance, check-ins, and reception</MenuItem>
              <MenuItem value="manager">Manager — broader gym operations access</MenuItem>
              <MenuItem value="admin">Admin — full access to all features</MenuItem>
              <MenuItem value="gym_admin">Gym Admin — manages a specific gym</MenuItem>
            </TextField>
            <TextField select label="Assign Gym" value={form.gymId} onChange={e => setForm({...form, gymId: e.target.value})} fullWidth>
              <MenuItem value="">— Select a gym (optional) —</MenuItem>
              {gyms.map(gym => <MenuItem key={gym._id} value={gym._id}>{gym.name}</MenuItem>)}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); setError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>
            {saving ? 'Creating...' : 'Create Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete Staff Account?</DialogTitle>
        <DialogContent>
          <Typography>This will permanently delete the account. This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
